/**
 * Dependency Injection Decorators
 *
 * Provides the @inject decorator and resolve() function for
 * accessing registered state and services from the DiContainer.
 */

import { DiContainer } from "./di-container";

/**
 * Property decorator for injecting dependencies from DiContainer.
 * Works on ANY class - LitElement components, services, utilities, etc.
 *
 * The dependency is resolved lazily when the property is accessed,
 * allowing the container to be initialized after class definition.
 *
 * Provide the contract type as a generic parameter for type safety.
 *
 * @example
 * class MyComponent extends SignalElement {
 *   @inject<ICounterState>("counterState")
 *   counterState!: ICounterState;
 *
 *   @inject<ICounterService>("counterService")
 *   counterService!: ICounterService;
 * }
 *
 * @example
 * class AnalyticsService {
 *   @inject<ICounterState>("counterState")
 *   counterState!: ICounterState;
 * }
 */
export function inject<T>(key: string) {
  // Legacy decorator signature: (proto, name) => void
  return (proto: object, name: string | symbol): void => {
    Object.defineProperty(proto, name, {
      get(): T {
        return DiContainer.get<T>(key);
      },
      enumerable: true,
      configurable: true,
    });
  };
}

/**
 * Function-based injection for cases where decorators aren't suitable.
 * Can be called anywhere: in methods, constructors, standalone functions,
 * or dynamic contexts.
 *
 * Provide the contract type as a generic parameter for type safety.
 *
 * @example
 * // In a method
 * someMethod() {
 *   const state = resolve<ICounterState>("counterState");
 *   console.log(state.count.get());
 * }
 *
 * @example
 * // In a standalone function
 * function logCurrentCount() {
 *   const state = resolve<ICounterState>("counterState");
 *   console.log("Current count:", state.count.get());
 * }
 *
 * @example
 * // With effect() for reactive updates
 * effect(() => {
 *   const count = resolve<ICounterState>("counterState").count.get();
 *   console.log("Count changed:", count);
 * });
 */
export function resolve<T>(key: string): T {
  return DiContainer.get<T>(key);
}
